import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  limit,
  getDocs,
  where,
} from "firebase/firestore";
import { projectFirestore } from "@/firebase/config";

// Store login log in Firestore
const storeLoginLog = async (userInfo, deviceInfo, ipInfo) => {
  try {
    const loginLogsCollection = collection(projectFirestore, "loginLogs");

    const loginLogData = {
      userId: userInfo.uid,
      email: userInfo.email,
      displayName: userInfo.displayName || null,
      emailVerified: userInfo.emailVerified,
      loginTimestamp: serverTimestamp(),
      deviceInfo: {
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        platform: deviceInfo.platform,
        language: deviceInfo.language,
        timezone: deviceInfo.timezone,
        screenResolution: deviceInfo.screenResolution,
        colorDepth: deviceInfo.colorDepth,
        cookieEnabled: deviceInfo.cookieEnabled,
        onlineStatus: deviceInfo.onlineStatus,
        userAgent: deviceInfo.userAgent,
      },
      locationInfo: {
        ip: ipInfo.ip,
        city: ipInfo.city,
        region: ipInfo.region,
        country: ipInfo.country,
        isp: ipInfo.isp,
      },
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(loginLogsCollection, loginLogData);
    return { success: true, logId: docRef.id };
  } catch (error) {
    console.error("Error storing login log:", error);
    return { success: false, error: error.message };
  }
};

// Get recent login logs
const getRecentLoginLogs = async (userId = null, limitCount = 10) => {
  try {
    const loginLogsCollection = collection(projectFirestore, "loginLogs");
    let q;

    if (userId) {
      q = query(
        loginLogsCollection,
        where("userId", "==", userId),
        orderBy("loginTimestamp", "desc"),
        limit(limitCount)
      );
    } else {
      q = query(
        loginLogsCollection,
        orderBy("loginTimestamp", "desc"),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);
    const logs = [];

    querySnapshot.forEach((doc) => {
      logs.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return { success: true, logs };
  } catch (error) {
    console.error("Error fetching login logs:", error);
    return { success: false, error: error.message };
  }
};

// Get login statistics
const getLoginStats = async (userId = null) => {
  try {
    const loginLogsCollection = collection(projectFirestore, "loginLogs");
    let q;

    if (userId) {
      q = query(
        loginLogsCollection,
        where("userId", "==", userId),
        orderBy("loginTimestamp", "desc")
      );
    } else {
      q = query(loginLogsCollection, orderBy("loginTimestamp", "desc"));
    }

    const querySnapshot = await getDocs(q);
    const logs = [];

    querySnapshot.forEach((doc) => {
      logs.push(doc.data());
    });

    // Calculate statistics
    const totalLogins = logs.length;
    const uniqueIPs = new Set(logs.map((log) => log.locationInfo?.ip)).size;
    const uniqueDevices = new Set(
      logs.map((log) => `${log.deviceInfo?.browser}-${log.deviceInfo?.os}`)
    ).size;
    const uniqueCountries = new Set(
      logs.map((log) => log.locationInfo?.country)
    ).size;

    // Get most common browser
    const browserCounts = {};
    logs.forEach((log) => {
      const browser = log.deviceInfo?.browser || "Unknown";
      browserCounts[browser] = (browserCounts[browser] || 0) + 1;
    });
    const mostCommonBrowser = Object.keys(browserCounts).reduce(
      (a, b) => (browserCounts[a] > browserCounts[b] ? a : b),
      "Unknown"
    );

    // Get most common country
    const countryCounts = {};
    logs.forEach((log) => {
      const country = log.locationInfo?.country || "Unknown";
      countryCounts[country] = (countryCounts[country] || 0) + 1;
    });
    const mostCommonCountry = Object.keys(countryCounts).reduce(
      (a, b) => (countryCounts[a] > countryCounts[b] ? a : b),
      "Unknown"
    );

    return {
      success: true,
      stats: {
        totalLogins,
        uniqueIPs,
        uniqueDevices,
        uniqueCountries,
        mostCommonBrowser,
        mostCommonCountry,
        browserCounts,
        countryCounts,
      },
    };
  } catch (error) {
    console.error("Error fetching login stats:", error);
    return { success: false, error: error.message };
  }
};

export { storeLoginLog, getRecentLoginLogs, getLoginStats };
